import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MoreHorizontal } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { ContributionDetailDialog } from '@/components/giving/contribution-detail-dialog';
import { VoidReasonDialog } from '@/components/giving/void-reason-dialog';
import { RecordContributionSaveOptions } from '@/components/giving/record-contribution-save-options';
import { useGivingSettings } from '@/hooks/use-giving-settings';
import { CollapsibleInsights } from '@/components/shared/collapsible-insights';
import {
  DateRangeFilter,
  LIST_FILTER_DATE_RANGE_PRESETS,
  resolvePreset,
  type DateRange,
} from '@/components/shared/date-range-filter';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { MemberNameLink } from '@/components/shared/member-name-link';
import { EntityCombobox } from '@/components/shared/entity-combobox';
import { FilterBar } from '@/components/shared/filter-bar';
import { FundSelect } from '@/components/shared/fund-select';
import { PageHeader } from '@/components/shared/page-header';
import { Pagination } from '@/components/shared/pagination';
import { SearchInput } from '@/components/shared/search-input';
import { StatCard } from '@/components/shared/stat-card';
import { Tip } from '@/components/shared/tip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Listbox } from '@/components/ui/listbox';
import { MoneyInput } from '@/components/ui/money-input';
import { MultiSelectListbox } from '@/components/ui/multi-select-listbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { hasPermission, PERMISSIONS } from '@/lib/permissions';
import { resolveDefaultFund } from '@/lib/giving-default-funds';
import {
  loadPledgesPagePreferences,
  PLEDGE_STATUS_VALUES,
  persistPledgesPagePreferences,
  resolveStoredPledgeDateRange,
  toPledgesPagePreferences,
  type PledgeStatusFilter,
} from '@/lib/pledges-page-preferences';
import {
  loadRecordContributionPreferences,
  persistRecordContributionPreferences,
} from '@/lib/record-contribution-preferences';
import { aggregateByCurrency, formatCurrencyAmountList, visibleCurrencyAmounts } from '@/lib/money-totals';
import { MetricMoneyCards } from '@/components/shared/money-totals-stack';
import { useChurchStore } from '@/stores/church-store';
import { usePreferences } from '@/stores/preferences-store';
import { ContributionAccountField } from '@/components/giving/contribution-account-field';
import {
  accountKindForMethod,
  CONTRIBUTION_METHODS,
  type ContributionMethod,
} from '@/lib/contribution-methods';
import { formatDate, formatMoney, formatNumber, formatPercent } from '@/utils/format';

interface Member {
  id: string;
  firstName: string;
  lastName: string;
}

interface Pledge {
  id: string;
  memberId: string;
  memberName: string;
  fundId: string;
  totalAmountMinor: number;
  committedAmountMinor: number;
  fulfilledAmountMinor: number;
  remainingAmountMinor: number;
  overFulfilled: boolean;
  currency: string;
  frequency: string;
  instalmentAmountMinor: number | null;
  startDate: string;
  endDate: string | null;
  status: string;
}

interface Project {
  id: string;
  name: string;
  description: string | null;
  fundId: string;
  targetAmountMinor: number;
  raisedAmountMinor: number;
  progressPercent: number;
  currency: string;
  startDate: string | null;
  deadline: string | null;
  status: string;
}

interface GiftRow {
  id: string;
  fundId: string;
  memberId?: string | null;
  giverName: string | null;
  identityHidden: boolean;
  amountMinor: number;
  currency: string;
  contributionDate: string;
  method: string;
  receiptNumber: number;
  note: string | null;
  isVoided: boolean;
  voidReason: string | null;
  voidedByName: string | null;
  canVoid: boolean;
  canEdit: boolean;
  updatedAt: string;
}

interface OffsetPage {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

const todayIso = () => new Date().toISOString().slice(0, 10);

const FREQUENCY_ANY = 'any';
const PLEDGE_FREQUENCIES = ['one_off', 'weekly', 'monthly', 'quarterly', 'annual'] as const;

const canRedeemPledge = (row: Pledge): boolean =>
  row.status === 'active' && row.remainingAmountMinor > 0;

const canCancelPledge = (row: Pledge): boolean =>
  row.status === 'active' || row.status === 'lapsed';

const canEditProject = (row: Project): boolean => row.status !== 'closed';

const projectFundLocked = (row: Project): boolean => row.raisedAmountMinor > 0;

export const PledgesProjectsPage = () => {
  const { t } = useTranslation('projects');
  const { t: tGiving } = useTranslation('giving');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const church = useChurchStore((state) => state.church);
  const membership = useChurchStore((state) => state.membership);
  const timezone = usePreferences((state) => state.timezone) ?? church?.defaultTimezone ?? 'UTC';

  const canRecord = hasPermission(membership, PERMISSIONS.GIVING_RECORD);
  const canManagePledges = hasPermission(membership, PERMISSIONS.GIVING_MANAGE_PLEDGES);
  const canEditProjects = hasPermission(membership, PERMISSIONS.PROJECTS_EDIT);
  const canCloseProjects = hasPermission(membership, PERMISSIONS.PROJECTS_CLOSE);
  const canVoidContribution = hasPermission(membership, PERMISSIONS.GIVING_VOID);

  const givingSettings = useGivingSettings();

  const [section, setSection] = useState('pledges');
  const [pledgeSearch, setPledgeSearch] = useState(
    () => loadPledgesPagePreferences().search,
  );
  const [pledgeFrequency, setPledgeFrequency] = useState(
    () => loadPledgesPagePreferences().frequency,
  );
  const [pledgeStatuses, setPledgeStatuses] = useState<PledgeStatusFilter[]>(
    () => loadPledgesPagePreferences().statuses,
  );
  const [pledgeDateRange, setPledgeDateRange] = useState<DateRange>(() => {
    const prefs = loadPledgesPagePreferences();
    return resolveStoredPledgeDateRange(prefs, 'UTC');
  });
  const [pledgePage, setPledgePage] = useState(1);
  const [pledgePageSize, setPledgePageSize] = useState(25);
  const [pledgeOpen, setPledgeOpen] = useState(false);
  const [projectOpen, setProjectOpen] = useState(false);
  const [detailTarget, setDetailTarget] = useState<Pledge | null>(null);
  const [redemptionPage, setRedemptionPage] = useState(1);
  const [projectDetail, setProjectDetail] = useState<Project | null>(null);
  const [projectGiftPage, setProjectGiftPage] = useState(1);
  const [redeemTarget, setRedeemTarget] = useState<Pledge | null>(null);
  const [memberId, setMemberId] = useState<string | null>(null);
  const [memberQuery, setMemberQuery] = useState('');
  const [fundId, setFundId] = useState<string | null>(null);
  const [fundCurrency, setFundCurrency] = useState<string | null>(null);
  const [amountMinor, setAmountMinor] = useState<number | null>(null);
  const [instalmentAmountMinor, setInstalmentAmountMinor] = useState<number | null>(null);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [frequency, setFrequency] = useState('one_off');
  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [projectFundId, setProjectFundId] = useState<string | null>(null);
  const [projectFundCurrency, setProjectFundCurrency] = useState<string | null>(null);
  const [targetMinor, setTargetMinor] = useState<number | null>(null);
  const [projectStartDate, setProjectStartDate] = useState<string | null>(null);
  const [projectDeadline, setProjectDeadline] = useState<string | null>(null);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [closeProjectTarget, setCloseProjectTarget] = useState<Project | null>(null);
  const [cancelPledgeTarget, setCancelPledgeTarget] = useState<Pledge | null>(null);
  const [detailContributionId, setDetailContributionId] = useState<string | null>(null);
  const [voidGiftTarget, setVoidGiftTarget] = useState<GiftRow | null>(null);
  const [voidGiftReason, setVoidGiftReason] = useState('');
  const [voidGiftPassword, setVoidGiftPassword] = useState('');
  const [voidGiftReasonTarget, setVoidGiftReasonTarget] = useState<GiftRow | null>(null);
  const [reopenProjectTarget, setReopenProjectTarget] = useState<Project | null>(null);
  const [redeemAmountMinor, setRedeemAmountMinor] = useState<number | null>(null);
  const [redeemDate, setRedeemDate] = useState<string | null>(todayIso());
  const [redeemMethod, setRedeemMethod] = useState<ContributionMethod>('cash');
  const [redeemAccountId, setRedeemAccountId] = useState<string | null>(null);
  const [redeemNote, setRedeemNote] = useState('');
  const [projectGiftTarget, setProjectGiftTarget] = useState<Project | null>(null);
  const [giftMemberId, setGiftMemberId] = useState<string | null>(null);
  const [giftMemberQuery, setGiftMemberQuery] = useState('');
  const [giftAmountMinor, setGiftAmountMinor] = useState<number | null>(null);
  const [giftDate, setGiftDate] = useState<string | null>(todayIso());
  const [giftMethod, setGiftMethod] = useState<ContributionMethod>('cash');
  const [giftAccountId, setGiftAccountId] = useState<string | null>(null);
  const [giftNote, setGiftNote] = useState('');
  const [savePreferences, setSavePreferences] = useState(() =>
    loadRecordContributionPreferences(church?.id),
  );
  const [pledgeFormKey, setPledgeFormKey] = useState(0);

  const resetPledgeForm = () => {
    setMemberId(null);
    setMemberQuery('');
    setAmountMinor(null);
    setInstalmentAmountMinor(null);
    setStartDate(todayIso());
    setEndDate(null);
    setFrequency('one_off');
    const defaults = resolveDefaultFund(givingSettings.data, 'pledge', activeFunds.data?.data);
    setFundId(defaults.fundId);
    setFundCurrency(defaults.currency);
  };

  const isRecurringPledge = frequency !== 'one_off';

  const pledgeInstalmentPreview = useMemo(() => {
    if (!isRecurringPledge || amountMinor == null || instalmentAmountMinor == null) return null;
    if (instalmentAmountMinor <= 0 || instalmentAmountMinor >= amountMinor) return null;
    const count = Math.ceil(amountMinor / instalmentAmountMinor);
    return t('form.instalmentPreview', { count });
  }, [amountMinor, instalmentAmountMinor, isRecurringPledge, t]);

  const pledgeFormValid = useMemo(() => {
    if (!memberId || !fundId || amountMinor == null || amountMinor <= 0 || !startDate) return false;
    if (!isRecurringPledge) return true;
    if (instalmentAmountMinor == null || instalmentAmountMinor <= 0) return false;
    if (instalmentAmountMinor >= amountMinor) return false;
    if (endDate && endDate < startDate) return false;
    return true;
  }, [
    amountMinor,
    endDate,
    fundId,
    instalmentAmountMinor,
    isRecurringPledge,
    memberId,
    startDate,
  ]);

  const members = useQuery({
    queryKey: ['people', 'members', 'picker', memberQuery],
    queryFn: () =>
      api.get<{ data: Member[] }>(
        `/people/members?pageSize=20${memberQuery ? `&q=${encodeURIComponent(memberQuery)}` : ''}`,
      ),
    enabled: pledgeOpen,
    placeholderData: keepPreviousData,
  });
  const giftMembers = useQuery({
    queryKey: ['people', 'members', 'picker', 'project-gift', giftMemberQuery],
    queryFn: () =>
      api.get<{ data: Member[] }>(
        `/people/members?pageSize=20${giftMemberQuery ? `&q=${encodeURIComponent(giftMemberQuery)}` : ''}`,
      ),
    enabled: projectGiftTarget !== null,
    placeholderData: keepPreviousData,
  });
  const pledgeSummary = useQuery({
    queryKey: ['giving', 'pledges', 'summary'],
    queryFn: () => api.get<{ data: Pledge[] }>('/giving/pledges?pageSize=100'),
  });
  const pledges = useQuery({
    queryKey: [
      'giving',
      'pledges',
      pledgePage,
      pledgePageSize,
      pledgeSearch,
      pledgeFrequency,
      pledgeStatuses,
      pledgeDateRange.from,
      pledgeDateRange.to,
    ],
    queryFn: () =>
      api.get<{ data: Pledge[]; page: OffsetPage }>('/giving/pledges', {
        query: {
          page: pledgePage,
          pageSize: pledgePageSize,
          q: pledgeSearch.trim() || undefined,
          frequency: pledgeFrequency === FREQUENCY_ANY ? undefined : pledgeFrequency,
          status: pledgeStatuses.length > 0 ? pledgeStatuses.join(',') : undefined,
          dateFrom: pledgeDateRange.from ?? undefined,
          dateTo: pledgeDateRange.to ?? undefined,
        },
      }),
    placeholderData: keepPreviousData,
  });
  const projects = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.get<{ data: Project[] }>('/projects?pageSize=50'),
  });
  const funds = useQuery({
    queryKey: ['giving', 'funds', 'labels'],
    queryFn: () => api.get<{ data: { id: string; name: string }[] }>('/giving/funds?pageSize=100'),
    enabled: detailTarget !== null || projectDetail !== null,
  });
  const activeFunds = useQuery({
    queryKey: ['giving', 'funds', 'active'],
    queryFn: () =>
      api.get<{ data: Array<{ id: string; defaultCurrency: string; isActive: boolean }> }>(
        '/giving/funds?pageSize=100&activeOnly=true',
      ),
    enabled: pledgeOpen || projectOpen,
  });
  const redemptions = useQuery({
    queryKey: ['giving', 'contributions', 'pledge', detailTarget?.id, redemptionPage],
    queryFn: () =>
      api.get<{ data: GiftRow[]; page: OffsetPage }>(
        `/giving/contributions?pledgeId=${detailTarget!.id}&page=${redemptionPage}&pageSize=20&includeVoided=true`,
      ),
    enabled: detailTarget !== null,
    placeholderData: keepPreviousData,
  });
  const projectGifts = useQuery({
    queryKey: ['giving', 'contributions', 'project', projectDetail?.id, projectGiftPage],
    queryFn: () =>
      api.get<{ data: GiftRow[]; page: OffsetPage }>(
        `/giving/contributions?projectId=${projectDetail!.id}&page=${projectGiftPage}&pageSize=20&includeVoided=true`,
      ),
    enabled: projectDetail !== null,
    placeholderData: keepPreviousData,
  });

  const memberOptions = useMemo(
    () =>
      (members.data?.data ?? []).map((member) => ({
        id: member.id,
        label: `${member.firstName} ${member.lastName}`.trim(),
      })),
    [members.data?.data],
  );
  const giftMemberOptions = useMemo(
    () =>
      (giftMembers.data?.data ?? []).map((member) => ({
        id: member.id,
        label: `${member.firstName} ${member.lastName}`.trim(),
      })),
    [giftMembers.data?.data],
  );
  const selectedMember = memberOptions.find((option) => option.id === memberId) ?? null;
  const selectedGiftMember =
    giftMemberOptions.find((option) => option.id === giftMemberId) ?? null;
  const pledgeCurrency = fundCurrency ?? church?.baseCurrency ?? 'GHS';
  const projectCurrency = projectFundCurrency ?? church?.baseCurrency ?? 'GHS';
  const baseCurrency = church?.baseCurrency ?? 'GHS';
  const canRecordProjectGift = (row: Project): boolean =>
    row.status === 'active' || row.status === 'funded';

  useEffect(() => {
    const prefs = loadPledgesPagePreferences(church?.id);
    setPledgeSearch(prefs.search);
    setPledgeFrequency(prefs.frequency);
    setPledgeStatuses(prefs.statuses);
    setPledgeDateRange(resolveStoredPledgeDateRange(prefs, timezone));
    setPledgePage(1);
  }, [church?.id, timezone]);

  useEffect(() => {
    persistPledgesPagePreferences(
      church?.id,
      toPledgesPagePreferences({
        search: pledgeSearch,
        frequency: pledgeFrequency,
        statuses: pledgeStatuses,
        dateRange: pledgeDateRange,
      }),
    );
  }, [church?.id, pledgeSearch, pledgeFrequency, pledgeStatuses, pledgeDateRange]);

  const pledgeStatusOptions = useMemo(
    () =>
      PLEDGE_STATUS_VALUES.map((value) => ({
        value,
        label: t(`pledges.status.${value}` as 'pledges.status.active'),
      })),
    [t],
  );

  const resetProjectForm = () => {
    setEditingProject(null);
    setProjectName('');
    setProjectDescription('');
    setTargetMinor(null);
    setProjectStartDate(null);
    setProjectDeadline(null);
  };

  const openProjectCreate = () => {
    resetProjectForm();
    setProjectOpen(true);
  };

  const openProjectEdit = (row: Project) => {
    setEditingProject(row);
    setProjectName(row.name);
    setProjectDescription(row.description ?? '');
    setProjectFundId(row.fundId);
    setProjectFundCurrency(row.currency);
    setTargetMinor(row.targetAmountMinor);
    setProjectStartDate(row.startDate);
    setProjectDeadline(row.deadline);
    setProjectOpen(true);
  };

  useEffect(() => {
    if (!pledgeOpen) return;
    setMemberId(null);
    setMemberQuery('');
    setAmountMinor(null);
    setInstalmentAmountMinor(null);
    setStartDate(todayIso());
    setEndDate(null);
    setFrequency('one_off');
    setPledgeFormKey((current) => current + 1);
  }, [pledgeOpen]);

  useEffect(() => {
    if (!pledgeOpen) return;
    const defaults = resolveDefaultFund(givingSettings.data, 'pledge', activeFunds.data?.data);
    setFundId(defaults.fundId);
    setFundCurrency(defaults.currency);
  }, [pledgeOpen, givingSettings.data, activeFunds.data?.data]);

  useEffect(() => {
    if (!projectOpen || editingProject) return;
    const defaults = resolveDefaultFund(givingSettings.data, 'project', activeFunds.data?.data);
    setProjectFundId(defaults.fundId);
    setProjectFundCurrency(defaults.currency);
  }, [projectOpen, editingProject, givingSettings.data, activeFunds.data?.data]);

  useEffect(() => {
    if (!redeemTarget) return;
    setRedeemAmountMinor(redeemTarget.remainingAmountMinor);
    setRedeemDate(todayIso());
    setRedeemMethod('cash');
    setRedeemNote('');
  }, [redeemTarget]);

  useEffect(() => {
    if (!projectGiftTarget) return;
    setGiftMemberId(null);
    setGiftMemberQuery('');
    setGiftAmountMinor(null);
    setGiftDate(todayIso());
    setGiftMethod('cash');
    setGiftNote('');
  }, [projectGiftTarget]);

  const detailId = detailTarget?.id;
  useEffect(() => {
    if (!detailId) return;
    const next =
      pledges.data?.data.find((row) => row.id === detailId) ??
      pledgeSummary.data?.data.find((row) => row.id === detailId);
    if (next) setDetailTarget(next);
  }, [pledges.data, pledgeSummary.data, detailId]);

  const projectDetailId = projectDetail?.id;
  useEffect(() => {
    if (!projectDetailId) return;
    const next = projects.data?.data.find((row) => row.id === projectDetailId);
    if (next) setProjectDetail(next);
  }, [projects.data, projectDetailId]);

  const createPledge = useMutation({
    mutationFn: () =>
      api.post<Pledge>('/giving/pledges', {
        memberId,
        fundId,
        totalAmountMinor: amountMinor,
        currency: pledgeCurrency,
        frequency,
        instalmentAmountMinor: isRecurringPledge ? instalmentAmountMinor : null,
        startDate,
        endDate: isRecurringPledge ? endDate : null,
      }),
    onSuccess: () => {
      toast.success(t('pledges.saved'));
      void queryClient.invalidateQueries({ queryKey: ['giving', 'pledges'] });
      if (savePreferences.addAnother) {
        resetPledgeForm();
        setPledgeFormKey((current) => current + 1);
        return;
      }
      setPledgeOpen(false);
      resetPledgeForm();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const redeemPledge = useMutation({
    mutationFn: () => {
      if (!redeemTarget || redeemAmountMinor == null || !redeemDate) {
        throw new Error('incomplete');
      }
      return api.post('/giving/contributions', {
        fundId: redeemTarget.fundId,
        memberId: redeemTarget.memberId,
        pledgeId: redeemTarget.id,
        amountMinor: redeemAmountMinor,
        currency: redeemTarget.currency,
        contributionDate: redeemDate,
        method: redeemMethod,
        financialPositionItemId: accountKindForMethod(redeemMethod) ? redeemAccountId : null,
        note: redeemNote.trim() ? redeemNote.trim() : null,
      });
    },
    onSuccess: async () => {
      const pledgeKey = redeemTarget?.id;
      toast.success(t('pledges.redeemed'));
      setRedeemTarget(null);
      setRedeemNote('');
      setRedemptionPage(1);
      if (!pledgeKey) return;

      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['giving', 'pledges'] }),
        queryClient.refetchQueries({
          queryKey: ['giving', 'contributions', 'pledge', pledgeKey],
        }),
      ]);
      void queryClient.invalidateQueries({ queryKey: ['giving', 'contributions'] });
      const pledgesResult = queryClient.getQueryData<{ data: Pledge[] }>(['giving', 'pledges']);
      const updated = pledgesResult?.data.find((row) => row.id === pledgeKey);
      if (updated) setDetailTarget(updated);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const recordProjectGift = useMutation({
    mutationFn: () => {
      if (!projectGiftTarget || giftAmountMinor == null || !giftDate) {
        throw new Error('incomplete');
      }
      return api.post('/giving/contributions', {
        fundId: projectGiftTarget.fundId,
        memberId: giftMemberId,
        projectId: projectGiftTarget.id,
        amountMinor: giftAmountMinor,
        currency: projectGiftTarget.currency,
        contributionDate: giftDate,
        method: giftMethod,
        financialPositionItemId: accountKindForMethod(giftMethod) ? giftAccountId : null,
        note: giftNote.trim() ? giftNote.trim() : null,
      });
    },
    onSuccess: async () => {
      const projectKey = projectGiftTarget?.id;
      toast.success(t('projects.giftRecorded'));
      setProjectGiftTarget(null);
      setGiftNote('');
      setProjectGiftPage(1);
      if (!projectKey) return;

      const [updatedProject] = await Promise.all([
        api.get<Project>(`/projects/${projectKey}`),
        queryClient.invalidateQueries({ queryKey: ['projects'] }),
        queryClient.invalidateQueries({ queryKey: ['giving', 'contributions'] }),
        queryClient.refetchQueries({
          queryKey: ['giving', 'contributions', 'project', projectKey],
        }),
      ]);
      setProjectDetail(updatedProject);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const cancelPledge = useMutation({
    mutationFn: (pledgeId: string) =>
      api.post<Pledge>(`/giving/pledges/${pledgeId}/cancel`, {}),
    onSuccess: (updated, pledgeId) => {
      toast.success(t('pledges.cancelled'));
      setCancelPledgeTarget(null);
      setDetailTarget((current) => (current?.id === pledgeId ? updated : current));
      void queryClient.invalidateQueries({ queryKey: ['giving', 'pledges'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const refreshGiftParents = async () => {
    const pledgeKey = detailTarget?.id;
    const projectKey = projectDetail?.id;

    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['giving', 'pledges'] }),
      queryClient.invalidateQueries({ queryKey: ['projects'] }),
      queryClient.invalidateQueries({ queryKey: ['giving', 'contributions'] }),
      pledgeKey
        ? queryClient.refetchQueries({
            queryKey: ['giving', 'contributions', 'pledge', pledgeKey],
          })
        : Promise.resolve(),
      projectKey
        ? queryClient.refetchQueries({
            queryKey: ['giving', 'contributions', 'project', projectKey],
          })
        : Promise.resolve(),
    ]);

    if (pledgeKey) {
      const updatedPledge = await api.get<Pledge>(`/giving/pledges/${pledgeKey}`);
      setDetailTarget(updatedPledge);
    }

    if (projectKey) {
      const updatedProject = await api.get<Project>(`/projects/${projectKey}`);
      setProjectDetail(updatedProject);
    }
  };

  const voidGift = useMutation({
    mutationFn: () =>
      api.post<GiftRow>(`/giving/contributions/${voidGiftTarget!.id}/void`, {
        reason: voidGiftReason,
        password: voidGiftPassword,
      }),
    onSuccess: async () => {
      toast.success(tGiving('voided'));
      setVoidGiftTarget(null);
      setVoidGiftReason('');
      setVoidGiftPassword('');
      await refreshGiftParents();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const createProject = useMutation({
    mutationFn: () =>
      api.post<Project>('/projects', {
        name: projectName,
        fundId: projectFundId,
        targetAmountMinor: targetMinor,
        currency: projectCurrency,
        description: projectDescription.trim() ? projectDescription.trim() : null,
        startDate: projectStartDate,
        deadline: projectDeadline,
        status: 'active',
      }),
    onSuccess: () => {
      toast.success(t('projects.saved'));
      setProjectOpen(false);
      resetProjectForm();
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const updateProject = useMutation({
    mutationFn: () => {
      if (!editingProject) throw new Error('missing project');
      const fundLocked = projectFundLocked(editingProject);
      return api.patch<Project>(`/projects/${editingProject.id}`, {
        name: projectName,
        description: projectDescription.trim() ? projectDescription.trim() : null,
        targetAmountMinor: targetMinor ?? undefined,
        ...(fundLocked || !projectFundId ? {} : { fundId: projectFundId }),
        startDate: projectStartDate,
        deadline: projectDeadline,
      });
    },
    onSuccess: (updated) => {
      toast.success(t('projects.updated'));
      setProjectOpen(false);
      resetProjectForm();
      setProjectDetail((current) => (current?.id === updated.id ? updated : current));
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const saveProject = editingProject ? updateProject : createProject;

  const closeProject = useMutation({
    mutationFn: (projectId: string) => api.post<Project>(`/projects/${projectId}/close`, {}),
    onSuccess: (updated, projectId) => {
      toast.success(t('projects.closed'));
      setCloseProjectTarget(null);
      setProjectDetail((current) => (current?.id === projectId ? updated : current));
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
      void queryClient.invalidateQueries({
        queryKey: ['giving', 'contributions', 'project', projectId],
      });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const reopenProject = useMutation({
    mutationFn: (projectId: string) => api.post<Project>(`/projects/${projectId}/reopen`, {}),
    onSuccess: (updated, projectId) => {
      toast.success(t('projects.reopened'));
      setReopenProjectTarget(null);
      setProjectDetail((current) => (current?.id === projectId ? updated : current));
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
      void queryClient.invalidateQueries({
        queryKey: ['giving', 'contributions', 'project', projectId],
      });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const pledgeColumns: DataTableColumn<Pledge>[] = [
    {
      id: 'startDate',
      header: t('pledges.columns.date'),
      cell: (row) => formatDate(row.startDate),
    },
    {
      id: 'member',
      header: t('pledges.columns.member'),
      cell: (row) => <MemberNameLink memberId={row.memberId} name={row.memberName} />,
    },
    {
      id: 'fulfilled',
      header: t('pledges.columns.fulfilled'),
      money: (row) => ({ amount: row.fulfilledAmountMinor, currency: row.currency }),
    },
    {
      id: 'total',
      header: t('pledges.columns.total'),
      money: (row) => ({ amount: row.totalAmountMinor, currency: row.currency }),
    },
    {
      id: 'frequency',
      header: t('pledges.columns.frequency'),
      cell: (row) => t(`frequency.${row.frequency}` as 'frequency.one_off'),
    },
    {
      id: 'status',
      header: t('pledges.columns.status'),
      cell: (row) =>
        row.overFulfilled
          ? `${t(`pledges.status.${row.status}` as 'pledges.status.active')} · ${t('pledges.over')}`
          : t(`pledges.status.${row.status}` as 'pledges.status.active'),
    },
  ];

  const projectColumns: DataTableColumn<Project>[] = [
    {
      id: 'name',
      header: t('projects.columns.name'),
      cell: (row) => row.name,
    },
    {
      id: 'raised',
      header: t('projects.columns.raised'),
      money: (row) => ({ amount: row.raisedAmountMinor, currency: row.currency }),
    },
    {
      id: 'target',
      header: t('projects.columns.target'),
      money: (row) => ({ amount: row.targetAmountMinor, currency: row.currency }),
    },
    {
      id: 'progress',
      header: t('projects.columns.progress'),
      cell: (row) => `${row.progressPercent}%`,
      align: 'right',
    },
    {
      id: 'status',
      header: t('projects.columns.status'),
      cell: (row) => t(`projects.status.${row.status}` as 'projects.status.active'),
    },
  ];

  const giftRowActions = (row: GiftRow) => {
    if (row.isVoided) {
      return (
        <Button size="sm" variant="outline" onClick={() => setVoidGiftReasonTarget(row)}>
          {tGiving('contributions.viewReason')}
        </Button>
      );
    }
    if (!canVoidContribution || !row.canVoid) return null;
    return (
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="outline" onClick={() => setVoidGiftTarget(row)}>
          {tGiving('contributions.void')}
        </Button>
      </div>
    );
  };

  const giftRowMemo = (row: GiftRow) => {
    const memo = row.note?.trim();
    return memo || null;
  };

  const redemptionColumns: DataTableColumn<GiftRow>[] = [
    {
      id: 'date',
      header: t('pledges.detail.columns.date'),
      cell: (row) => formatDate(row.contributionDate),
    },
    {
      id: 'amount',
      header: t('pledges.detail.columns.amount'),
      money: (row) => ({ amount: row.amountMinor, currency: row.currency }),
    },
    {
      id: 'method',
      header: t('pledges.detail.columns.method'),
      cell: (row) => tGiving(`methods.${row.method}` as 'methods.cash'),
    },
    {
      id: 'receipt',
      header: t('pledges.detail.columns.receipt'),
      cell: (row) => String(row.receiptNumber),
      align: 'right',
    },
    {
      id: 'status',
      header: tGiving('contributions.columns.status'),
      cell: (row) =>
        row.isVoided ? (
          <Badge variant="danger">{tGiving('contributions.voided')}</Badge>
        ) : (
          '—'
        ),
      hideBelowMd: true,
    },
  ];

  const projectGiftColumns: DataTableColumn<GiftRow>[] = [
    {
      id: 'date',
      header: t('projects.detail.columns.date'),
      cell: (row) => formatDate(row.contributionDate),
    },
    {
      id: 'giver',
      header: t('projects.detail.columns.giver'),
      cell: (row) =>
        row.identityHidden ? (
          t('projects.detail.anonymous')
        ) : (
          <MemberNameLink memberId={row.memberId} name={row.giverName ?? ''} />
        ),
    },
    {
      id: 'amount',
      header: t('projects.detail.columns.amount'),
      money: (row) => ({ amount: row.amountMinor, currency: row.currency }),
    },
    {
      id: 'method',
      header: t('projects.detail.columns.method'),
      cell: (row) => tGiving(`methods.${row.method}` as 'methods.cash'),
    },
    {
      id: 'receipt',
      header: t('projects.detail.columns.receipt'),
      cell: (row) => String(row.receiptNumber),
      align: 'right',
    },
    {
      id: 'status',
      header: tGiving('contributions.columns.status'),
      cell: (row) =>
        row.isVoided ? (
          <Badge variant="danger">{tGiving('contributions.voided')}</Badge>
        ) : (
          '—'
        ),
      hideBelowMd: true,
    },
  ];

  const pledgeRows = pledges.data?.data ?? [];
  const pledgeSummaryRows = pledgeSummary.data?.data ?? [];

  const pledgeFilterCount = useMemo(() => {
    let count = 0;
    if (pledgeSearch.trim()) count += 1;
    if (pledgeFrequency !== FREQUENCY_ANY) count += 1;
    if (pledgeStatuses.length > 0) count += 1;
    if (pledgeDateRange.preset !== 'allTime') count += 1;
    return count;
  }, [pledgeDateRange.preset, pledgeFrequency, pledgeSearch, pledgeStatuses.length]);

  const pledgeMetrics = useMemo(() => {
    const active = pledgeSummaryRows.filter((row) => row.status === 'active');
    return {
      activeCount: active.length,
      pledged: aggregateByCurrency(
        pledgeSummaryRows,
        (row) => row.committedAmountMinor,
        (row) => row.currency,
      ),
      given: aggregateByCurrency(
        pledgeSummaryRows,
        (row) => row.fulfilledAmountMinor,
        (row) => row.currency,
      ),
      remaining: aggregateByCurrency(
        active,
        (row) => row.remainingAmountMinor,
        (row) => row.currency,
      ),
    };
  }, [pledgeSummaryRows]);

  const projectRows = projects.data?.data ?? [];

  const projectMetrics = useMemo(() => {
    const raised = aggregateByCurrency(
      projectRows,
      (row) => row.raisedAmountMinor,
      (row) => row.currency,
    );
    const target = aggregateByCurrency(
      projectRows,
      (row) => row.targetAmountMinor,
      (row) => row.currency,
    );
    const progressByCurrency = raised
      .map((raisedItem) => {
        const targetItem = target.find((item) => item.currency === raisedItem.currency);
        if (!targetItem || targetItem.amountMinor <= 0) return null;
        return {
          currency: raisedItem.currency,
          progress: raisedItem.amountMinor / targetItem.amountMinor,
        };
      })
      .filter((item): item is { currency: string; progress: number } => item !== null);
    return {
      raised,
      target,
      progressByCurrency,
    };
  }, [projectRows]);

  const visibleProjectMoney = useMemo(
    () => ({
      raised: visibleCurrencyAmounts(projectMetrics.raised, baseCurrency),
      target: visibleCurrencyAmounts(projectMetrics.target, baseCurrency),
    }),
    [baseCurrency, projectMetrics.raised, projectMetrics.target],
  );

  const visibleProjectCurrencies = useMemo(
    () => [
      ...new Set([
        ...visibleProjectMoney.raised.map((row) => row.currency),
        ...visibleProjectMoney.target.map((row) => row.currency),
      ]),
    ],
    [visibleProjectMoney.raised, visibleProjectMoney.target],
  );

  const visibleProjectProgress = useMemo(
    () =>
      projectMetrics.progressByCurrency.filter((item) =>
        visibleProjectCurrencies.includes(item.currency),
      ),
    [projectMetrics.progressByCurrency, visibleProjectCurrencies],
  );

  const fundName =
    funds.data?.data.find(
      (fund) => fund.id === (detailTarget?.fundId ?? projectDetail?.fundId),
    )?.name ?? '—';
  const redemptionPageMeta = redemptions.data?.page;
  const projectGiftPageMeta = projectGifts.data?.page;
  const projectRemainingMinor = projectDetail
    ? Math.max(0, projectDetail.targetAmountMinor - projectDetail.raisedAmountMinor)
    : 0;
  const paginationLabels = {
    showing: ({ from, to, total }: { from: number; to: number; total: number }) =>
      tCommon('pagination.showing', { from, to, total }),
    perPage: tCommon('pagination.perPage'),
    first: tCommon('pagination.first'),
    previous: tCommon('pagination.previous'),
    next: tCommon('pagination.next'),
    last: tCommon('pagination.last'),
  };

  const renderProjectRowActions = (row: Project) => {
    const showEdit = canEditProjects && canEditProject(row);
    const showClose = canCloseProjects;
    if (!showEdit && !showClose) return null;

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            aria-label={tCommon('table.actions')}
          >
            <MoreHorizontal className="size-4" aria-hidden />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {showEdit ? (
            <DropdownMenuItem onClick={() => openProjectEdit(row)}>
              {t('projects.edit')}
            </DropdownMenuItem>
          ) : null}
          {showClose ? (
            row.status === 'closed' ? (
              <DropdownMenuItem onClick={() => setReopenProjectTarget(row)}>
                {t('projects.reopen')}
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem destructive onClick={() => setCloseProjectTarget(row)}>
                {t('projects.close')}
              </DropdownMenuItem>
            )
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return (
    <div>
      <PageHeader
        title={t('title')}
        description={t('description')}
        tipId="projects.pledgeVsGift"
        headerActions={
          section === 'pledges' ? (
            <Button onClick={() => setPledgeOpen(true)}>{t('pledges.create')}</Button>
          ) : (
            <Button onClick={openProjectCreate}>{t('projects.create')}</Button>
          )
        }
      />

      <Tabs value={section} onValueChange={setSection}>
        <TabsList>
          <TabsTrigger value="pledges">{t('tabs.pledges')}</TabsTrigger>
          <TabsTrigger value="projects">{t('tabs.projects')}</TabsTrigger>
        </TabsList>

        <TabsContent value="pledges" className="space-y-3">
          <CollapsibleInsights
            storageKey="trinity.insights.pledges-projects.pledges"
            summary={[
              {
                label: t('pledges.insights.pledged'),
                value: formatCurrencyAmountList(pledgeMetrics.pledged, baseCurrency),
              },
              {
                label: t('pledges.insights.given'),
                value: formatCurrencyAmountList(pledgeMetrics.given, baseCurrency),
              },
              {
                label: t('pledges.insights.active'),
                value: pledgeSummary.isLoading ? '…' : formatNumber(pledgeMetrics.activeCount),
              },
            ]}
          >
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <MetricMoneyCards
                label={t('pledges.insights.pledged')}
                items={pledgeMetrics.pledged}
                loading={pledgeSummary.isLoading}
                baseCurrency={baseCurrency}
              />
              <MetricMoneyCards
                label={t('pledges.insights.given')}
                items={pledgeMetrics.given}
                loading={pledgeSummary.isLoading}
                baseCurrency={baseCurrency}
              />
              <MetricMoneyCards
                label={t('pledges.insights.remaining')}
                items={pledgeMetrics.remaining}
                loading={pledgeSummary.isLoading}
                baseCurrency={baseCurrency}
              />
              <StatCard
                label={t('pledges.insights.active')}
                value={
                  pledgeSummary.isLoading ? undefined : formatNumber(pledgeMetrics.activeCount)
                }
                loading={pledgeSummary.isLoading}
              />
            </div>
          </CollapsibleInsights>
          <FilterBar
            appliedCount={pledgeFilterCount}
            onClearAll={() => {
              setPledgeSearch('');
              setPledgeFrequency(FREQUENCY_ANY);
              setPledgeStatuses([]);
              setPledgeDateRange({
                preset: 'allTime',
                ...resolvePreset('allTime', timezone),
              });
              setPledgePage(1);
            }}
            labels={{
              filters: t('pledges.filters.title'),
              clearAll: t('pledges.filters.clearAll'),
              applied: (count) => t('pledges.filters.applied', { count }),
            }}
            leading={
              <SearchInput
                value={pledgeSearch}
                onChange={(value) => {
                  setPledgeSearch(value);
                  setPledgePage(1);
                }}
                placeholder={t('pledges.filters.search')}
                clearLabel={tCommon('search.clear')}
                className="max-w-none sm:w-56"
                aria-label={t('pledges.filters.search')}
              />
            }
          >
            {({ popoverPortal }) => (
              <>
                <div className="w-44 min-w-[10rem]">
                  <Listbox
                    value={pledgeFrequency}
                    onChange={(value) => {
                      setPledgeFrequency(value ?? FREQUENCY_ANY);
                      setPledgePage(1);
                    }}
                    options={[
                      { value: FREQUENCY_ANY, label: t('pledges.filters.frequencyAny') },
                      ...PLEDGE_FREQUENCIES.map((value) => ({
                        value,
                        label: t(`frequency.${value}`),
                      })),
                    ]}
                    popoverPortal={popoverPortal}
                  />
                </div>
                <div className="w-48 min-w-[11rem]">
                  <MultiSelectListbox
                    value={pledgeStatuses}
                    onChange={(next) => {
                      setPledgeStatuses(next);
                      setPledgePage(1);
                    }}
                    options={pledgeStatusOptions}
                    placeholder={t('pledges.filters.statusAny')}
                    summaryLabel={(count) => t('pledges.filters.statusCount', { count })}
                    clearAllLabel={tCommon('action.clearSelection')}
                    popoverPortal={popoverPortal}
                    aria-label={t('pledges.filters.status')}
                  />
                </div>
                <DateRangeFilter
                  value={pledgeDateRange}
                  onChange={(range) => {
                    setPledgeDateRange(range);
                    setPledgePage(1);
                  }}
                  labels={{
                    today: tCommon('date.today'),
                    yesterday: tCommon('date.yesterday'),
                    thisWeek: tCommon('date.thisWeek'),
                    lastWeek: tCommon('date.lastWeek'),
                    thisMonth: tCommon('date.thisMonth'),
                    lastMonth: tCommon('date.lastMonth'),
                    thisQuarter: tCommon('date.thisQuarter'),
                    thisYear: tCommon('date.thisYear'),
                    lastYear: tCommon('date.lastYear'),
                    allTime: tCommon('date.allTime'),
                    custom: tCommon('date.custom'),
                    from: t('pledges.filters.dateFrom'),
                    to: t('pledges.filters.dateTo'),
                  }}
                  presets={LIST_FILTER_DATE_RANGE_PRESETS}
                  popoverPortal={popoverPortal}
                />
              </>
            )}
          </FilterBar>
          <DataTable
            rows={pledgeRows}
            columns={pledgeColumns}
            rowKey={(row) => row.id}
            loading={pledges.isLoading}
            empty={
              <p className="text-body-sm text-fg-secondary">
                {pledgeFilterCount > 0 ? t('pledges.emptyFiltered') : t('pledges.empty')}
              </p>
            }
            onRowClick={(row) => {
              setRedemptionPage(1);
              setDetailTarget(row);
            }}
            rowActions={
              canRecord
                ? (row) =>
                    canRedeemPledge(row) ? (
                      <Button size="sm" variant="outline" onClick={() => setRedeemTarget(row)}>
                        {t('pledges.redeem')}
                      </Button>
                    ) : null
                : undefined
            }
            actionsHeader={canRecord ? tCommon('table.actions') : undefined}
          />
          {pledges.data?.page && pledges.data.page.totalPages > 1 ? (
            <Pagination
              page={pledges.data.page.page}
              pageSize={pledges.data.page.pageSize}
              totalItems={pledges.data.page.totalItems}
              totalPages={pledges.data.page.totalPages}
              onPageChange={setPledgePage}
              onPageSizeChange={(next) => {
                setPledgePageSize(next);
                setPledgePage(1);
              }}
              labels={paginationLabels}
            />
          ) : null}
        </TabsContent>

        <TabsContent value="projects" className="space-y-3">
          <CollapsibleInsights
            storageKey="trinity.insights.pledges-projects.projects"
            summary={[
              {
                label: t('projects.insights.raised'),
                value: formatCurrencyAmountList(projectMetrics.raised, baseCurrency),
              },
              {
                label: t('projects.insights.target'),
                value: formatCurrencyAmountList(projectMetrics.target, baseCurrency),
              },
              {
                label: t('projects.insights.progress'),
                value:
                  visibleProjectProgress.length === 0
                    ? '…'
                    : visibleProjectProgress
                        .map((item) => `${item.currency} ${formatPercent(item.progress)}`)
                        .join(' · '),
              },
            ]}
          >
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <MetricMoneyCards
                label={t('projects.insights.raised')}
                items={projectMetrics.raised}
                loading={projects.isLoading}
                baseCurrency={baseCurrency}
              />
              <MetricMoneyCards
                label={t('projects.insights.target')}
                items={projectMetrics.target}
                loading={projects.isLoading}
                baseCurrency={baseCurrency}
              />
              {visibleProjectCurrencies.length <= 1 ? (
                <StatCard
                  label={t('projects.insights.remaining')}
                  money={
                    visibleProjectMoney.raised[0] && visibleProjectMoney.target[0]
                      ? {
                          amount: Math.max(
                            0,
                            visibleProjectMoney.target[0].amountMinor -
                              visibleProjectMoney.raised[0].amountMinor,
                          ),
                          currency: visibleProjectMoney.target[0].currency,
                        }
                      : undefined
                  }
                  loading={projects.isLoading}
                />
              ) : (
                visibleProjectCurrencies.map((currency) => {
                  const raisedItem = visibleProjectMoney.raised.find(
                    (row) => row.currency === currency,
                  );
                  const targetItem = visibleProjectMoney.target.find(
                    (row) => row.currency === currency,
                  );
                  return (
                    <StatCard
                      key={currency}
                      label={`${t('projects.insights.remaining')} (${currency})`}
                      money={
                        raisedItem && targetItem
                          ? {
                              amount: Math.max(0, targetItem.amountMinor - raisedItem.amountMinor),
                              currency,
                            }
                          : undefined
                      }
                      loading={projects.isLoading}
                    />
                  );
                })
              )}
              <StatCard
                label={t('projects.insights.progress')}
                value={
                  visibleProjectProgress.length === 0
                    ? undefined
                    : visibleProjectProgress.length === 1
                      ? formatPercent(visibleProjectProgress[0]!.progress)
                      : visibleProjectProgress
                          .map((item) => `${item.currency} ${formatPercent(item.progress)}`)
                          .join(' · ')
                }
                loading={projects.isLoading}
              />
            </div>
          </CollapsibleInsights>
          <DataTable
            rows={projectRows}
            columns={projectColumns}
            rowKey={(row) => row.id}
            loading={projects.isLoading}
            empty={<p className="text-body-sm text-fg-secondary">{t('projects.empty')}</p>}
            onRowClick={(row) => {
              setProjectGiftPage(1);
              setProjectDetail(row);
            }}
            rowActions={renderProjectRowActions}
            actionsHeader={tCommon('table.actions')}
          />
        </TabsContent>
      </Tabs>

      <ConfirmDialog
        open={cancelPledgeTarget !== null}
        onOpenChange={(next) => {
          if (!next) setCancelPledgeTarget(null);
        }}
        title={t('pledges.cancelConfirm.title', {
          name: cancelPledgeTarget?.memberName ?? '',
        })}
        description={t('pledges.cancelConfirm.description')}
        confirmLabel={t('pledges.cancelConfirm.confirm')}
        cancelLabel={tCommon('action.cancel')}
        loading={cancelPledge.isPending}
        destructive
        onConfirm={() => {
          if (cancelPledgeTarget) cancelPledge.mutate(cancelPledgeTarget.id);
        }}
      />

      <ConfirmDialog
        open={closeProjectTarget !== null}
        onOpenChange={(next) => {
          if (!next) setCloseProjectTarget(null);
        }}
        title={t('confirm.closeProject.title', { name: closeProjectTarget?.name ?? '' })}
        description={t('confirm.closeProject.description')}
        confirmLabel={t('confirm.closeProject.confirm')}
        cancelLabel={tCommon('action.cancel')}
        loading={closeProject.isPending}
        onConfirm={() => {
          if (closeProjectTarget) closeProject.mutate(closeProjectTarget.id);
        }}
      />

      <ConfirmDialog
        open={reopenProjectTarget !== null}
        onOpenChange={(next) => {
          if (!next) setReopenProjectTarget(null);
        }}
        title={t('confirm.reopenProject.title', { name: reopenProjectTarget?.name ?? '' })}
        description={t('confirm.reopenProject.description')}
        confirmLabel={t('confirm.reopenProject.confirm')}
        cancelLabel={tCommon('action.cancel')}
        loading={reopenProject.isPending}
        onConfirm={() => {
          if (reopenProjectTarget) reopenProject.mutate(reopenProjectTarget.id);
        }}
      />

      <Dialog
        open={pledgeOpen}
        onOpenChange={(next) => {
          setPledgeOpen(next);
          if (!next) resetPledgeForm();
        }}
      >
        <DialogContent closeLabel={tCommon('action.close')} className="overflow-visible">
          <form
            key={pledgeFormKey}
            className="min-h-0"
            onSubmit={(event) => {
              event.preventDefault();
              createPledge.mutate();
            }}
          >
            <DialogBody
              className="max-h-[calc(92dvh-3rem)]"
              contentClassName="flex flex-col gap-4"
            >
              <DialogHeader>
                <DialogTitle>{t('pledges.create')}</DialogTitle>
              </DialogHeader>
              <div className="flex flex-col gap-1.5">
                <Label>{t('form.member')}</Label>
                <EntityCombobox
                  value={memberId}
                  onChange={setMemberId}
                  options={memberOptions}
                  selected={selectedMember}
                  onQueryChange={setMemberQuery}
                  popoverPortal="dialog"
                  clearLabel={tCommon('action.clearSelection')}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>{t('form.fund')}</Label>
                <FundSelect
                  value={fundId}
                  onChange={(id, fund) => {
                    setFundId(id);
                    setFundCurrency(fund?.defaultCurrency ?? null);
                  }}
                  popoverPortal="dialog"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>{t('form.totalPledge')}</Label>
                <p className="text-caption text-fg-secondary">{t('form.totalPledgeHint')}</p>
                <MoneyInput value={amountMinor} onChange={setAmountMinor} currency={pledgeCurrency} />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>{t('form.frequency')}</Label>
                <Listbox
                  value={frequency}
                  onChange={(value) => {
                    const next = value ?? 'one_off';
                    setFrequency(next);
                    if (next === 'one_off') {
                      setInstalmentAmountMinor(null);
                      setEndDate(null);
                    }
                  }}
                  options={['one_off', 'weekly', 'monthly', 'quarterly', 'annual'].map((value) => ({
                    value,
                    label: t(`frequency.${value}` as 'frequency.one_off'),
                  }))}
                  popoverPortal="dialog"
                />
              </div>
              {isRecurringPledge ? (
                <div className="flex flex-col gap-1.5">
                  <Label>{t('form.instalmentAmount')}</Label>
                  <p className="text-caption text-fg-secondary">{t('form.instalmentAmountHint')}</p>
                  <MoneyInput
                    value={instalmentAmountMinor}
                    onChange={setInstalmentAmountMinor}
                    currency={pledgeCurrency}
                  />
                  {pledgeInstalmentPreview ? (
                    <p className="text-caption text-fg-secondary">{pledgeInstalmentPreview}</p>
                  ) : null}
                </div>
              ) : null}
              <div className="flex flex-col gap-1.5">
                <Label>{t('form.startDate')}</Label>
                <DatePicker value={startDate} onChange={setStartDate} popoverPortal="dialog" />
              </div>
              {isRecurringPledge ? (
                <div className="flex flex-col gap-1.5">
                  <Label>{t('form.endDate')}</Label>
                  <p className="text-caption text-fg-secondary">{t('form.endDateHint')}</p>
                  <DatePicker value={endDate} onChange={setEndDate} popoverPortal="dialog" />
                </div>
              ) : null}
              <RecordContributionSaveOptions
                addAnother={savePreferences.addAnother}
                onAddAnotherChange={(addAnother) => {
                  const next = { ...savePreferences, addAnother };
                  setSavePreferences(next);
                  persistRecordContributionPreferences(church?.id, next);
                }}
                disabled={createPledge.isPending}
              />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setPledgeOpen(false)}>
                  {tCommon('action.cancel')}
                </Button>
                <Button
                  type="submit"
                  loading={createPledge.isPending}
                  disabled={!pledgeFormValid}
                >
                  {t('form.savePledge')}
                </Button>
              </DialogFooter>
            </DialogBody>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={detailTarget !== null}
        onOpenChange={(next) => {
          if (!next) setDetailTarget(null);
        }}
      >
        <DialogContent size="2xl" closeLabel={tCommon('action.close')} className="overflow-visible">
          <DialogHeader>
            {detailTarget ? (
              <>
                <div className="flex items-start justify-between gap-3">
                  <DialogTitle className="min-w-0 truncate">
                    {detailTarget.memberName || t('pledges.detail.title')}
                  </DialogTitle>
                  <Badge variant={detailTarget.status === 'active' ? 'brand' : 'neutral'}>
                    {detailTarget.overFulfilled
                      ? t('pledges.over')
                      : t(`pledges.status.${detailTarget.status}` as 'pledges.status.active')}
                  </Badge>
                </div>
                <DialogDescription>
                  {t('pledges.detail.description', {
                    date: formatDate(detailTarget.startDate),
                  })}
                </DialogDescription>
              </>
            ) : (
              <DialogTitle>{t('pledges.detail.title')}</DialogTitle>
            )}
          </DialogHeader>
          {detailTarget ? (
            <>
              <DialogBody>
                <div className="space-y-6">
                  <div className="grid gap-3 sm:grid-cols-3">
                    <StatCard
                      label={t('pledges.detail.pledged')}
                      money={{
                        amount: detailTarget.totalAmountMinor,
                        currency: detailTarget.currency,
                      }}
                    />
                    <StatCard
                      label={t('pledges.detail.given')}
                      money={{
                        amount: detailTarget.fulfilledAmountMinor,
                        currency: detailTarget.currency,
                      }}
                    />
                    <StatCard
                      label={t('pledges.detail.remaining')}
                      money={{
                        amount: detailTarget.remainingAmountMinor,
                        currency: detailTarget.currency,
                      }}
                    />
                  </div>
                  <dl className="grid gap-3 text-body-sm sm:grid-cols-2">
                    <div>
                      <dt className="text-fg-secondary">{t('form.member')}</dt>
                      <dd className="font-medium text-fg">{detailTarget.memberName || '—'}</dd>
                    </div>
                    <div>
                      <dt className="text-fg-secondary">{t('form.fund')}</dt>
                      <dd className="font-medium text-fg">{fundName}</dd>
                    </div>
                    <div>
                      <dt className="text-fg-secondary">{t('form.frequency')}</dt>
                      <dd className="font-medium text-fg">
                        {t(`frequency.${detailTarget.frequency}` as 'frequency.one_off')}
                      </dd>
                    </div>
                    {detailTarget.frequency !== 'one_off' && detailTarget.instalmentAmountMinor ? (
                      <div>
                        <dt className="text-fg-secondary">{t('form.instalmentAmount')}</dt>
                        <dd className="font-medium text-fg">
                          {formatMoney(
                            detailTarget.instalmentAmountMinor,
                            detailTarget.currency,
                            { showSymbol: true },
                          )}
                        </dd>
                      </div>
                    ) : null}
                    <div>
                      <dt className="text-fg-secondary">{t('pledges.columns.date')}</dt>
                      <dd className="font-medium text-fg">{formatDate(detailTarget.startDate)}</dd>
                    </div>
                    {detailTarget.endDate ? (
                      <div>
                        <dt className="text-fg-secondary">{t('form.endDate')}</dt>
                        <dd className="font-medium text-fg">{formatDate(detailTarget.endDate)}</dd>
                      </div>
                    ) : null}
                  </dl>
                  <div className="space-y-2">
                    <h3 className="text-body font-medium text-fg">
                      {t('pledges.detail.redemptions')}
                    </h3>
                    <DataTable
                      rows={redemptions.data?.data ?? []}
                      columns={redemptionColumns}
                      rowKey={(row) => row.id}
                      loading={redemptions.isLoading}
                      rowTooltip={giftRowMemo}
                      rowTooltipPortal="dialog"
                      onRowClick={(row) => setDetailContributionId(row.id)}
                      rowActions={canVoidContribution ? giftRowActions : undefined}
                      actionsHeader={
                        canVoidContribution ? tCommon('table.actions') : undefined
                      }
                      empty={
                        <p className="text-body-sm text-fg-secondary">
                          {t('pledges.detail.redemptionsEmpty')}
                        </p>
                      }
                    />
                    {redemptionPageMeta && redemptionPageMeta.totalPages > 1 ? (
                      <Pagination
                        page={redemptionPageMeta.page}
                        pageSize={redemptionPageMeta.pageSize}
                        totalItems={redemptionPageMeta.totalItems}
                        totalPages={redemptionPageMeta.totalPages}
                        onPageChange={setRedemptionPage}
                        labels={paginationLabels}
                      />
                    ) : null}
                  </div>
                </div>
              </DialogBody>
              <DialogFooter>
                {canManagePledges && canCancelPledge(detailTarget) ? (
                  <Button
                    type="button"
                    variant="outline"
                    className="mr-auto text-destructive hover:text-destructive"
                    onClick={() => setCancelPledgeTarget(detailTarget)}
                  >
                    {t('pledges.cancel')}
                  </Button>
                ) : null}
                <Button type="button" variant="outline" onClick={() => setDetailTarget(null)}>
                  {tCommon('action.close')}
                </Button>
                {canRecord && canRedeemPledge(detailTarget) ? (
                  <Button
                    type="button"
                    onClick={() => {
                      setRedeemTarget(detailTarget);
                    }}
                  >
                    {t('pledges.redeem')}
                  </Button>
                ) : null}
              </DialogFooter>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog
        open={projectDetail !== null}
        onOpenChange={(next) => {
          if (!next) setProjectDetail(null);
        }}
      >
        <DialogContent size="2xl" closeLabel={tCommon('action.close')} className="overflow-visible">
          <DialogHeader>
            {projectDetail ? (
              <>
                <div className="flex items-start justify-between gap-3">
                  <DialogTitle className="min-w-0 truncate">{projectDetail.name}</DialogTitle>
                  <Badge variant={projectDetail.status === 'active' ? 'brand' : 'neutral'}>
                    {t(`projects.status.${projectDetail.status}` as 'projects.status.active')}
                  </Badge>
                </div>
                <DialogDescription>
                  {t('projects.detail.description', {
                    progress: projectDetail.progressPercent,
                  })}
                </DialogDescription>
              </>
            ) : (
              <DialogTitle>{t('projects.detail.title')}</DialogTitle>
            )}
          </DialogHeader>
          {projectDetail ? (
            <>
              <DialogBody>
                <div className="space-y-6">
                  <div className="grid gap-3 sm:grid-cols-3">
                    <StatCard
                      label={t('projects.detail.target')}
                      money={{
                        amount: projectDetail.targetAmountMinor,
                        currency: projectDetail.currency,
                      }}
                    />
                    <StatCard
                      label={t('projects.detail.raised')}
                      tipId="projects.fundingProgress"
                      money={{
                        amount: projectDetail.raisedAmountMinor,
                        currency: projectDetail.currency,
                      }}
                    />
                    <StatCard
                      label={t('projects.detail.remaining')}
                      money={{
                        amount: projectRemainingMinor,
                        currency: projectDetail.currency,
                      }}
                    />
                  </div>
                  <dl className="grid gap-3 text-body-sm sm:grid-cols-2">
                    <div>
                      <dt className="text-fg-secondary">{t('form.fund')}</dt>
                      <dd className="font-medium text-fg">{fundName}</dd>
                    </div>
                    <div>
                      <dt className="text-fg-secondary">{t('projects.columns.progress')}</dt>
                      <dd className="font-medium text-fg">{projectDetail.progressPercent}%</dd>
                    </div>
                    {projectDetail.startDate ? (
                      <div>
                        <dt className="text-fg-secondary">{t('form.startDate')}</dt>
                        <dd className="font-medium text-fg">
                          {formatDate(projectDetail.startDate)}
                        </dd>
                      </div>
                    ) : null}
                    {projectDetail.deadline ? (
                      <div>
                        <dt className="text-fg-secondary">{t('projects.detail.deadline')}</dt>
                        <dd className="font-medium text-fg">
                          {formatDate(projectDetail.deadline)}
                        </dd>
                      </div>
                    ) : null}
                    {projectDetail.description ? (
                      <div className="sm:col-span-2">
                        <dt className="text-fg-secondary">{t('projects.detail.about')}</dt>
                        <dd className="font-medium text-fg">{projectDetail.description}</dd>
                      </div>
                    ) : null}
                  </dl>
                  <div className="space-y-2">
                    <h3 className="text-body font-medium text-fg">{t('projects.detail.gifts')}</h3>
                    <DataTable
                      rows={projectGifts.data?.data ?? []}
                      columns={projectGiftColumns}
                      rowKey={(row) => row.id}
                      loading={projectGifts.isLoading}
                      rowTooltip={giftRowMemo}
                      rowTooltipPortal="dialog"
                      onRowClick={(row) => setDetailContributionId(row.id)}
                      rowActions={canVoidContribution ? giftRowActions : undefined}
                      actionsHeader={
                        canVoidContribution ? tCommon('table.actions') : undefined
                      }
                      empty={
                        <p className="text-body-sm text-fg-secondary">
                          {t('projects.detail.giftsEmpty')}
                        </p>
                      }
                    />
                    {projectGiftPageMeta && projectGiftPageMeta.totalPages > 1 ? (
                      <Pagination
                        page={projectGiftPageMeta.page}
                        pageSize={projectGiftPageMeta.pageSize}
                        totalItems={projectGiftPageMeta.totalItems}
                        totalPages={projectGiftPageMeta.totalPages}
                        onPageChange={setProjectGiftPage}
                        labels={paginationLabels}
                      />
                    ) : null}
                  </div>
                </div>
              </DialogBody>
              <DialogFooter>
                {canEditProjects && canEditProject(projectDetail) ? (
                  <Button
                    type="button"
                    variant="outline"
                    className="mr-auto"
                    onClick={() => openProjectEdit(projectDetail)}
                  >
                    {t('projects.edit')}
                  </Button>
                ) : null}
                <Button type="button" variant="outline" onClick={() => setProjectDetail(null)}>
                  {tCommon('action.close')}
                </Button>
                {canCloseProjects ? (
                  projectDetail.status === 'closed' ? (
                    <Button
                      type="button"
                      onClick={() => setReopenProjectTarget(projectDetail)}
                    >
                      {t('projects.reopen')}
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setCloseProjectTarget(projectDetail)}
                    >
                      {t('projects.close')}
                    </Button>
                  )
                ) : null}
                {canRecord && canRecordProjectGift(projectDetail) ? (
                  <Button type="button" onClick={() => setProjectGiftTarget(projectDetail)}>
                    {t('projects.recordGift')}
                  </Button>
                ) : null}
              </DialogFooter>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog
        open={projectGiftTarget !== null}
        onOpenChange={(next) => {
          if (!next) setProjectGiftTarget(null);
        }}
      >
        <DialogContent closeLabel={tCommon('action.close')} className="overflow-visible">
          <DialogHeader>
            <DialogTitle>{t('projects.recordGiftTitle')}</DialogTitle>
            {projectGiftTarget ? <Tip tipId="projects.recordGift" /> : null}
          </DialogHeader>
          <form
            className="flex flex-col gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              if (giftAmountMinor == null || !giftDate) return;
              recordProjectGift.mutate();
            }}
          >
            <div className="flex flex-col gap-1.5">
              <Label>
                {tGiving('form.member')}{' '}
                <span className="text-fg-secondary">({tGiving('form.memberOptional')})</span>
              </Label>
              <EntityCombobox
                value={giftMemberId}
                onChange={setGiftMemberId}
                options={giftMemberOptions}
                selected={selectedGiftMember}
                onQueryChange={setGiftMemberQuery}
                loading={giftMembers.isPending && !giftMembers.data}
                popoverPortal="dialog"
                clearLabel={tCommon('action.clearSelection')}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>{t('form.amount')}</Label>
              <MoneyInput
                value={giftAmountMinor}
                onChange={setGiftAmountMinor}
                currency={projectGiftTarget?.currency ?? projectCurrency}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>{tGiving('form.date')}</Label>
              <DatePicker value={giftDate} onChange={setGiftDate} popoverPortal="dialog" />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>{tGiving('form.method')}</Label>
              <Listbox
                value={giftMethod}
                onChange={(next) => {
                  setGiftMethod((next as ContributionMethod) ?? 'cash');
                  setGiftAccountId(null);
                }}
                options={CONTRIBUTION_METHODS.map((method) => ({
                  value: method,
                  label: tGiving(`methods.${method}`),
                }))}
                popoverPortal="dialog"
              />
            </div>
            <ContributionAccountField
              open={projectGiftTarget !== null}
              method={giftMethod}
              currency={projectGiftTarget?.currency ?? projectCurrency}
              value={giftAccountId}
              onChange={setGiftAccountId}
            />
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="project-gift-memo">{t('pledges.memo')}</Label>
              <Textarea
                id="project-gift-memo"
                value={giftNote}
                onChange={(event) => setGiftNote(event.target.value)}
                placeholder={t('pledges.memoPlaceholder')}
                rows={2}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setProjectGiftTarget(null)}>
                {tCommon('action.cancel')}
              </Button>
              <Button
                type="submit"
                loading={recordProjectGift.isPending}
                disabled={
                  giftAmountMinor == null ||
                  giftAmountMinor <= 0 ||
                  !giftDate ||
                  Boolean(accountKindForMethod(giftMethod) && !giftAccountId)
                }
              >
                {t('projects.recordGift')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={redeemTarget !== null}
        onOpenChange={(next) => {
          if (!next) setRedeemTarget(null);
        }}
      >
        <DialogContent closeLabel={tCommon('action.close')} className="overflow-visible">
          <DialogHeader>
            <DialogTitle>{t('pledges.redeemTitle')}</DialogTitle>
          </DialogHeader>
          <form
            className="flex flex-col gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              if (redeemAmountMinor == null || !redeemDate) return;
              redeemPledge.mutate();
            }}
          >
            {redeemTarget ? (
              <div className="space-y-1 text-body-sm">
                <p>
                  <span className="text-fg-secondary">{t('form.member')}: </span>
                  {redeemTarget.memberName || '—'}
                </p>
                <Tip tipId="projects.redeem" />
                <p className="text-fg-secondary">
                  {formatMoney(redeemTarget.remainingAmountMinor, redeemTarget.currency)}
                </p>
              </div>
            ) : null}
            <div className="flex flex-col gap-1.5">
              <Label>{t('form.amount')}</Label>
              <MoneyInput
                value={redeemAmountMinor}
                onChange={setRedeemAmountMinor}
                currency={redeemTarget?.currency ?? pledgeCurrency}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>{tGiving('form.date')}</Label>
              <DatePicker value={redeemDate} onChange={setRedeemDate} popoverPortal="dialog" />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>{tGiving('form.method')}</Label>
              <Listbox
                value={redeemMethod}
                onChange={(next) => {
                  setRedeemMethod((next as ContributionMethod) ?? 'cash');
                  setRedeemAccountId(null);
                }}
                options={CONTRIBUTION_METHODS.map((method) => ({
                  value: method,
                  label: tGiving(`methods.${method}`),
                }))}
                popoverPortal="dialog"
              />
            </div>
            <ContributionAccountField
              open={redeemTarget !== null}
              method={redeemMethod}
              currency={redeemTarget?.currency ?? pledgeCurrency}
              value={redeemAccountId}
              onChange={setRedeemAccountId}
            />
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="redeem-memo">{t('pledges.memo')}</Label>
              <Textarea
                id="redeem-memo"
                value={redeemNote}
                onChange={(event) => setRedeemNote(event.target.value)}
                placeholder={t('pledges.memoPlaceholder')}
                rows={2}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setRedeemTarget(null)}>
                {tCommon('action.cancel')}
              </Button>
              <Button
                type="submit"
                loading={redeemPledge.isPending}
                disabled={
                  redeemAmountMinor == null ||
                  redeemAmountMinor <= 0 ||
                  !redeemDate ||
                  Boolean(accountKindForMethod(redeemMethod) && !redeemAccountId)
                }
              >
                {t('pledges.redeemConfirm')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={projectOpen}
        onOpenChange={(next) => {
          setProjectOpen(next);
          if (!next) resetProjectForm();
        }}
      >
        <DialogContent closeLabel={tCommon('action.close')} className="overflow-visible">
          <form
            className="min-h-0"
            onSubmit={(event) => {
              event.preventDefault();
              saveProject.mutate();
            }}
          >
            <DialogBody
              className="max-h-[calc(92dvh-3rem)]"
              contentClassName="flex flex-col gap-4"
            >
              <DialogHeader>
                <DialogTitle>
                  {editingProject ? t('projects.edit') : t('projects.create')}
                </DialogTitle>
              </DialogHeader>
              <div className="flex flex-col gap-1.5">
                <Label>{t('form.name')}</Label>
                <Input
                  value={projectName}
                  onChange={(event) => setProjectName(event.target.value)}
                  required
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>{t('form.description')}</Label>
                <Textarea
                  value={projectDescription}
                  onChange={(event) => setProjectDescription(event.target.value)}
                  placeholder={t('form.descriptionPlaceholder')}
                  rows={2}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>{t('form.fund')}</Label>
                <FundSelect
                  value={projectFundId}
                  onChange={(id, fund) => {
                    setProjectFundId(id);
                    setProjectFundCurrency(fund?.defaultCurrency ?? null);
                  }}
                  popoverPortal="dialog"
                  disabled={editingProject ? projectFundLocked(editingProject) : false}
                />
                {editingProject && projectFundLocked(editingProject) ? (
                  <p className="text-body-sm text-fg-secondary">{t('form.fundLockedHint')}</p>
                ) : null}
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>{t('form.target')}</Label>
                <MoneyInput value={targetMinor} onChange={setTargetMinor} currency={projectCurrency} />
                {editingProject && projectFundLocked(editingProject) ? (
                  <p className="text-body-sm text-fg-secondary">
                    {t('form.targetMinHint', {
                      amount: formatMoney(
                        editingProject.raisedAmountMinor,
                        editingProject.currency,
                        { showSymbol: true },
                      ),
                    })}
                  </p>
                ) : null}
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <Label>{t('form.startDate')}</Label>
                  <DatePicker
                    value={projectStartDate}
                    onChange={setProjectStartDate}
                    popoverPortal="dialog"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label>{t('form.deadline')}</Label>
                  <DatePicker
                    value={projectDeadline}
                    onChange={setProjectDeadline}
                    popoverPortal="dialog"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setProjectOpen(false);
                    resetProjectForm();
                  }}
                >
                  {tCommon('action.cancel')}
                </Button>
                <Button
                  type="submit"
                  loading={saveProject.isPending}
                  disabled={
                    !projectName ||
                    !projectFundId ||
                    targetMinor == null ||
                    (editingProject != null &&
                      projectFundLocked(editingProject) &&
                      targetMinor < editingProject.raisedAmountMinor)
                  }
                >
                  {editingProject ? t('form.saveChanges') : t('form.saveProject')}
                </Button>
              </DialogFooter>
            </DialogBody>
          </form>
        </DialogContent>
      </Dialog>

      <ContributionDetailDialog
        contributionId={detailContributionId}
        open={Boolean(detailContributionId)}
        onOpenChange={(open) => {
          if (!open) setDetailContributionId(null);
        }}
      />

      <Dialog
        open={Boolean(voidGiftTarget)}
        onOpenChange={(next) => {
          if (!next) {
            setVoidGiftTarget(null);
            setVoidGiftReason('');
            setVoidGiftPassword('');
          }
        }}
      >
        <DialogContent closeLabel={tCommon('action.close')}>
          <DialogHeader>
            <DialogTitle>{tGiving('contributions.voidTitle')}</DialogTitle>
          </DialogHeader>
          <form
            className="flex flex-col gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              if (!voidGiftReason.trim() || !voidGiftPassword) return;
              voidGift.mutate();
            }}
          >
            <Tip tipId="giving.contribution.void" />
            <p className="text-body-sm text-fg-secondary">
              {tGiving('contributions.voidDescription')}
            </p>
            <div className="flex flex-col gap-1.5">
              <Label>{tGiving('contributions.voidReason')}</Label>
              <Textarea
                value={voidGiftReason}
                onChange={(event) => setVoidGiftReason(event.target.value)}
                required
                rows={3}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="gift-void-password">{tGiving('contributions.voidPassword')}</Label>
              <Input
                id="gift-void-password"
                type="password"
                value={voidGiftPassword}
                onChange={(event) => setVoidGiftPassword(event.target.value)}
                autoComplete="current-password"
                required
              />
              <p className="text-caption text-fg-secondary">{tGiving('contributions.voidPasswordHint')}</p>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setVoidGiftTarget(null);
                  setVoidGiftReason('');
                  setVoidGiftPassword('');
                }}
              >
                {tCommon('action.cancel')}
              </Button>
              <Button
                type="submit"
                variant="destructive"
                loading={voidGift.isPending}
                disabled={!voidGiftReason.trim() || !voidGiftPassword}
              >
                {tGiving('contributions.voidConfirm')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <VoidReasonDialog
        open={Boolean(voidGiftReasonTarget)}
        onClose={() => setVoidGiftReasonTarget(null)}
        reason={voidGiftReasonTarget?.voidReason}
        voidedByName={voidGiftReasonTarget?.voidedByName}
      />
    </div>
  );
};
